import { ApiResponseError } from "@/lib/error";
import { operations, components } from "@/openapi/generated";
import { api } from "@/openapi/client";
import { getCacheTags } from "@/lib/cache-utils";
import { charityQueryKeys } from "@/app/admin/charities/_api/charity-query-keys";

export type ListCharitiesParams = operations["listCharities"]["parameters"]["query"];
export type ListCharitiesResponse = operations["listCharities"]["responses"]["200"]["content"]["*/*"];

export async function getAdminCharities(
  query: ListCharitiesParams,
  client: typeof api = api
): Promise<ListCharitiesResponse> {
  const { data, error } = await client.GET("/charities", {
    params: { query },
    next: { tags: getCacheTags(charityQueryKeys.list({
      searchTerm: query?.searchTerm ?? undefined,
      status: query?.status ?? undefined,
      page: query?.page ?? undefined,
      size: query?.size ?? undefined,
      sort: query?.sort ?? undefined,
    })) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAdminCharities");
  return data;
}

export type GetCharityParams = operations["getCharity"]["parameters"]["path"]["id"];
export type GetCharityResponse = operations["getCharity"]["responses"]["200"]["content"]["*/*"];

export async function getAdminCharity(
  id: GetCharityParams,
  client: typeof api = api
): Promise<GetCharityResponse> {
  const { data, error } = await client.GET("/charities/{id}/details", {
    params: { path: { id } },
    next: { tags: getCacheTags(charityQueryKeys.detail(id)) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAdminCharity");
  return data;
}

export type ListProjectsParams = operations["listProjects"]["parameters"]["path"]["charityId"];
export type ListProjectsResponse = operations["listProjects"]["responses"]["200"]["content"]["*/*"];

export async function getAdminCharityProjects(
  charityId: ListProjectsParams,
  client: typeof api = api
): Promise<ListProjectsResponse> {
  const { data, error } = await client.GET("/charities/{charityId}/projects", {
    params: { path: { charityId } },
    next: { tags: getCacheTags(charityQueryKeys.projects(charityId)) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getAdminCharityProjects");
  return data;
}

export type GetFieldsOfWorkResponse = operations["getFieldsOfWork"]["responses"]["200"]["content"]["*/*"];

export async function getFieldsOfWork(
  client: typeof api = api
): Promise<GetFieldsOfWorkResponse> {
  const { data, error } = await client.GET("/charities/fields-of-work", {
    next: { tags: getCacheTags(charityQueryKeys.fieldsOfWork) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getFieldsOfWork");
  return data;
}

export type CertificateAdminDto = components["schemas"]["CertificateAdminDto"];

export async function getCertificates(
  client: typeof api = api
): Promise<CertificateAdminDto[]> {
  const { data, error } = await client.GET("/charities/certificates", {
    next: { tags: getCacheTags(charityQueryKeys.certificates) },
    cache: "force-cache",
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getCertificates");
  return data;
}
